/**
 * Canonical Clinio UI typography (Design system V2).
 *
 * Tokens live as CSS variables on `:root` (`--font-sans`, `--type-*`).
 * These class names only apply family / size / weight / line-height.
 * Colour and spacing stay with the calling component.
 *
 * Tailwind `text-xs` / `text-sm` / `text-base` / `text-lg` / `text-2xl` /
 * `text-3xl` are wired to the same CSS variables in `tailwind.config.js`.
 * Prefer these role tokens for new UI.
 */

export const TYPE = {
  /** Patient name (H1) — 32/40 (28/36 under 760), 600, -0.02em. */
  display: 'type-display',
  /** Staff page title — 28/36, 600. */
  title: 'type-title',
  /** Card / slide-over / modal title (H2) — 20/28, 600, -0.015em. */
  heading: 'type-heading',
  /** Page subtitle / supporting line — 15/24, 400. */
  subtitle: 'type-subtitle',
  /** Section heading (H3) — 16/24, 600. */
  section: 'type-section',
  /** Toolbar / tab / button chrome — 14/20, 500. */
  control: 'type-control',
  /** Body copy — 15/24, 400. */
  body: 'type-body',
  /** Form labels — 13/20, 600. */
  label: 'type-label',
  /** Card title — same as heading (20/28, 600). */
  card: 'type-card',
  /** Helper / hint — 13/20, 400, typically `--muted`. */
  small: 'type-small',
  /** Chip and meta — 12/16, 600. */
  meta: 'type-meta',
  /** Captions, kbd — 12/16, 500. */
  caption: 'type-caption',
  /**
   * Entered form values: 16px on small viewports (avoids iOS focus-zoom),
   * 15px from the `sm` breakpoint up.
   */
  inputValue: 'type-input-value',
} as const;

export type TypeRole = keyof typeof TYPE;

/** Limited weight scale — do not introduce 700/800 for ordinary UI. */
export const TYPE_WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
} as const;

export const TYPE_SIZE_PX = {
  display: 32,
  title: 28,
  heading: 20,
  subtitle: 15,
  section: 16,
  control: 14,
  body: 15,
  label: 13,
  card: 20,
  small: 13,
  meta: 12,
  caption: 12,
  button: 14,
} as const;

export const PAGE_TITLE_TYPE = `${TYPE.title} text-ds-text`;

export const PAGE_SUBTITLE_TYPE = `${TYPE.subtitle} text-ds-muted`;

export const PAGE_SUBTITLE_STACKED_TYPE = `${TYPE.body} mt-1 text-ds-muted`;

export const PORTAL_PAGE_TITLE = `${TYPE.display} text-ds-text`;

export const PORTAL_PAGE_SUBTITLE = `${TYPE.subtitle} text-ds-muted`;

export const MODAL_TITLE_TYPE = `${TYPE.heading} text-ds-text`;

export const FORM_LABEL_TYPE = `${TYPE.label} text-ds-text`;

export const FORM_HINT_TYPE = `${TYPE.small} text-ds-muted`;

export const FORM_ERROR_TYPE = `${TYPE.small} font-medium text-ds-red-ink`;

/** Table header — 13/20 600 muted, sentence case (never uppercase). */
export const TABLE_HEADER_TYPE = `${TYPE.label} text-ds-muted`;

export const TABLE_CELL_TYPE = `${TYPE.body} text-ds-text`;

export const TABLE_CELL_MUTED_TYPE = `${TYPE.body} text-ds-muted`;

/** In-page section heading — larger than body, smaller than the page title. */
export const UI_SECTION = `${TYPE.section} text-ds-text`;

/** Normal muted body copy (same size as body). */
export const UI_MUTED = `${TYPE.body} text-ds-muted`;

/** Secondary / supporting text. */
export const UI_SECONDARY = `${TYPE.small} text-ds-muted`;

export const TABLE_SECONDARY_TYPE = UI_SECONDARY;

/** Compact status chips. */
export const STATUS_BADGE_TYPE = `${TYPE.meta} font-medium`;

/** Codes and hex values only — not amounts, times, dates or CPR. */
export const TYPE_MONO = 'font-mono';

/** Amounts, times, dates, CPR. */
export const TYPE_TABULAR = 'tabular-nums';
